/* eslint-disable prettier/prettier */
import { useState, useContext, useCallback, useEffect, Suspense } from "react";
import {
	AppBar,
	Avatar,
	Accordion,
	AccordionSummary,
	CssBaseline,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Popover,
	Toolbar,
	Tooltip,
	makeStyles
} from "@material-ui/core";
import { Outlet, useNavigate } from "react-router-dom";
import AccountCircle from "@material-ui/icons/AccountCircle";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import MenuIcon from "@material-ui/icons/Menu";

import { AuthContext, UIContext } from "../../../../contexts";
import { LazyCustomDialog } from "../../../shared";
import SIDEBAR_OPTIONS from "../../../../constants/sidebarOptions";
import { Tab } from "../../../../@types/sidebarOptions.types";
import { DialogProps } from "../../../../@types/dialogProps.types";
import { bataLogo } from "../../../../assets";
import useRouteParam from "../../../../hooks/useRouteParam";
import { api } from "../../../../utils/api";

import "../../admin/adminSidebar/adminSidebar.css";
import "../../admin/adminSidebar/adminSidebarScroll.css";

const DRAWER_WIDTH = 250;

// eslint-disable-next-line arrow-parens
const useStyles = makeStyles((theme) => ({
	application: {
		display: "flex"
	},
	menuButton: {
		marginRight: theme.spacing(2),
		backgroundColor: "#EEEEEE",
		color: "black"
	},
	toolbar: theme.mixins.toolbar,
	content: {
		flexGrow: 1,
		minHeight: "100vh",
		padding: theme.spacing(3)
	},
	avatarColor: {
		color: "black",
		padding: 40
	},
	grow: {
		flexGrow: 1
	}
}));

/**
 * Shell for the USER role.
 *
 * Deliberately much smaller than the admin sidebars: a USER has a single page, so there
 * is no group switcher, no breadcrumb trail into other areas and no profile page to
 * navigate to - only the scan report list and logout.
 */
const UserSidebar = (): JSX.Element => {
	const classes = useStyles();
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const { dashboardHeader } = useContext(UIContext);
	const groupNumber = useRouteParam("gn");
	const storeNumber = useRouteParam("sn");
	const [group, setGroup] = useState<any>(null);
	const [drawerMenuOpen, setDrawerMenuOpen] = useState(true);
	const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH);
	const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
	const [logoutDialogProps, setLogoutDialogProps] = useState<DialogProps>({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});

	const getGroupDetails = useCallback(async () => {
		if (!groupNumber) {
			return;
		}
		try {
			const response = await api.group.getGroupDetails({ group_number: groupNumber });
			if (response) {
				setGroup(response);
			}
		} catch (err) {
			console.log("Failed to fetch group details", err);
		}
	}, [groupNumber]);

	useEffect(() => {
		getGroupDetails();
	}, [getGroupDetails]);

	const handleDrawerMenuToggle = useCallback(() => {
		setDrawerMenuOpen(!drawerMenuOpen);
		setDrawerWidth(drawerMenuOpen ? 0 : DRAWER_WIDTH);
	}, [drawerMenuOpen]);

	const handleProfileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => setAnchorEl(null);

	const handleLogoutClick = useCallback(() => {
		localStorage.clear();
		setAnchorEl(null);
		setLogoutDialogProps(
			Object.assign({}, logoutDialogProps, {
				openDialog: true,
				title: "Thank you",
				content: "You have successfully logged out of CuraFoot CRM!",
				actions: [
					{
						label: "Okay",
						callback: () => {
							navigate("/login");
							setLogoutDialogProps(Object.assign({}, logoutDialogProps, { openDialog: false }));
						}
					}
				]
			})
		);
	}, [logoutDialogProps, navigate]);

	const drawer = (
		<div id="store-admin" className="store-admin">
			<div className="avatar-icon-container">
				<IconButton onClick={handleDrawerMenuToggle}>
					<Avatar style={{ width: 24, height: 24, backgroundColor: "#25a18e" }}>
						<ChevronLeftIcon />
					</Avatar>
				</IconButton>
			</div>
			<div className="sidebar-logo-container">
				<img src={group ? group.logo : bataLogo} alt="" />
			</div>
			<div className={classes.toolbar} />
			<div className="welcome-container">
				<div className="dashboard-type-text" id="dashboard-type-text">
					{group && group.group_name ? group.group_name : "Store"}
				</div>
				<Avatar alt="" className={classes.avatarColor}>
					<div className="avatar-text">
						{user && user.first_name && user.last_name
							? user.first_name.charAt(0) + user.last_name.charAt(0)
							: ""}
					</div>
				</Avatar>
				<div className="welcome-text" id="welcome-text">
					{user && user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : null}
				</div>
			</div>
			<Divider />
			<List>
				{SIDEBAR_OPTIONS.USER.map((tab: Tab, tabIndex: number) => (
					<Accordion
						className="list-item-accordion"
						key={tabIndex}
						expanded={false}
						onClick={() => navigate(`${tab.route}?gn=${groupNumber}&sn=${storeNumber}`)}
					>
						<AccordionSummary
							expandIcon={<div />}
							aria-controls="panel1a-content"
							id="panel1a-header"
							className="list-item-accordion-summary"
						>
							<div style={{ marginRight: 10 }}>{tab.icon()}</div>
							<div>{tab.caption}</div>
						</AccordionSummary>
					</Accordion>
				))}
			</List>
		</div>
	);

	return (
		<div className="employer-sidebar-container" id="employer-sidebar-container">
			<div className={classes.application}>
				<Suspense fallback={<div />}>
					<LazyCustomDialog dialogProps={logoutDialogProps} />
				</Suspense>
				<CssBaseline />
				<AppBar
					position="fixed"
					style={{
						width: `calc(100% - ${drawerWidth}px)`,
						marginLeft: drawerWidth,
						backgroundColor: "#EEEEEE",
						color: "black"
					}}
				>
					<Toolbar className="toolbar">
						{!drawerMenuOpen && (
							<IconButton
								color="inherit"
								aria-label="open drawer"
								edge="start"
								onClick={handleDrawerMenuToggle}
								className={classes.menuButton}
							>
								<MenuIcon />
							</IconButton>
						)}
						<div className="appbar-header" id="appbar-header">
							{dashboardHeader}
						</div>
						<div className={classes.grow} />
						<Tooltip title="Profile" enterDelay={500} leaveDelay={200}>
							<IconButton
								edge="end"
								aria-label="account of current user"
								aria-haspopup="true"
								onClick={handleProfileMenuOpen}
								color="inherit"
							>
								<AccountCircle style={{ color: "#4e4e4e" }} />
							</IconButton>
						</Tooltip>
					</Toolbar>
				</AppBar>
				<Popover
					open={Boolean(anchorEl)}
					anchorEl={anchorEl}
					onClose={handleMenuClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					transformOrigin={{ vertical: "top", horizontal: "center" }}
				>
					<div className="profile-menu">
						<List>
							<ListItem button onClick={handleLogoutClick}>
								<ListItemAvatar>
									<Avatar className="list-avatar">
										<ExitToAppIcon />
									</Avatar>
								</ListItemAvatar>
								<ListItemText primary="Logout" />
							</ListItem>
						</List>
					</div>
				</Popover>
				<nav aria-label="sidebar">
					<Drawer
						variant="persistent"
						open={drawerMenuOpen}
						onClose={handleDrawerMenuToggle}
						ModalProps={{ keepMounted: true }}
						style={{ width: drawerWidth, backgroundColor: "#003545" }}
					>
						{drawer}
					</Drawer>
				</nav>
				<main className={classes.content + " main"}>
					<div className={classes.toolbar} />
					<Outlet />
				</main>
			</div>
		</div>
	);
};

export default UserSidebar;
